import React from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx'; // eslint-disable-line
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import ThumbsUpDown from '@material-ui/icons/ThumbsUpDown';
import SmileIcon from '@material-ui/icons/Mood';
import CloseIcon from '@material-ui/icons/Close';
import SadIcon from '@material-ui/icons/SentimentVeryDissatisfied';
import ReviewForm from './ReviewForm';

const style = makeStyles((theme) => ({
  arrow: {
    color: 'rgba(97, 97, 97, 0.5)',
  },
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    border: '1px solid rgba(97, 97, 97, 0.2)',
  },
  button: {
    [theme.breakpoints.down('sm')]: {
      padding: '12px 4px',
    },
  },
  link: {
    textDecoration: 'underline',
    color: theme.palette.common.white,
    cursor: 'pointer',
  },
  icons: {
    textAlign: 'center',
  },
  icon: {
    fontSize: '3rem',
  },
  right: {
    textAlign: 'right',
  },
  recommended: {
    border: `1px solid ${theme.palette.primary.main}`,

    '& svg': {
      color: theme.palette.primary.main,
    },
  },
  notrecommended: {
    border: `1px solid ${theme.palette.secondary.main}`,

    '& svg': {
      color: theme.palette.secondary.main,
    },
  },
}));

const ReviewTooltip = (props) => {
  const classes = style();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('xs'));

  const [open, setOpened] = React.useState(props.open);
  const [data, setData] = React.useState({
    ...props.initialValues,
  });
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const handleClose = () => setOpened(false);
  const handleOpen = () => setOpened(true);
  const onSubmit = (values = {}, close = false) => {
    props.onSubmit({
      ...data,
      ...values,
    });
    setData({
      ...data,
      ...values,
    });

    if (close) {
      handleClose();
      setDialogOpen(false);
    }
  };
  const setRecommended = (v) => {
    setData({
      recommended: v,
      details: {
        slow: null,
        calendar_outdated: null,
        price_outdated: null,
        fast: null,
        friendly: null,
      },
      extra: null,
    });
    onSubmit({
      recommended: v,
      details: {
        slow: null,
        calendar_outdated: null,
        price_outdated: null,
        fast: null,
        friendly: null,
      },
      extra: null,
    });

    if (isSmall) {
      // if the screen is small we will open the dialog
      // and close the tooltip
      setDialogOpen(true);
      handleClose();
    }
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <>
      {
        isSmall && (
          <Dialog
            onClose={handleDialogClose}
            open={dialogOpen}
            aria-describedby="review-dialog"
          >
            <DialogContent>
              <ReviewForm
                onSubmit={onSubmit}
                initialValues={data}
              />
            </DialogContent>
          </Dialog>
        )
      }
      <Tooltip
        arrow
        open={open}
        id="review-tooltip"
        classes={{ arrow: classes.arrow, tooltip: classes.tooltip }}
        onClose={(e) => {
          if (e && e.type === 'touchend') {
            return;
          }

          handleClose();
        }}
        onOpen={handleOpen}
        interactive
        disableFocusListener
        disableHoverListener
        leaveDelay={10000}
        title={(
          <>
            <div className={classes.right}>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </div>
            <Typography align="center">
              <FormattedMessage
                id="conversation.review.heading"
                defaultMessage="Would you recommend {name} as a host?"
                values={{ name: props.host.first_name }}
              />
            </Typography>
            <div className={classes.icons}>
              <IconButton
                onClick={() => setRecommended(true)}
                className={clsx({ [classes.recommended]: data.recommended === true })}
              >
                <SmileIcon className={classes.icon} />
              </IconButton>
              <IconButton
                onClick={() => setRecommended(false)}
                className={clsx({ [classes.notrecommended]: data.recommended === false })}
              >
                <SadIcon className={classes.icon} />
              </IconButton>
            </div>
            <Typography variant="body2" align="center">
              <FormattedMessage
                id="conversaton.review.anonymous"
                defaultMessage="This review is anonymous. Your name will not be revealed to the host"
              />
            </Typography>

            {
              !isSmall && data.recommended !== null && (
                <>
                  <hr />
                  <ReviewForm
                    onSubmit={onSubmit}
                    initialValues={data}
                  />
                </>
              )
            }
          </>
        )}
      >
        <IconButton
          onClick={handleOpen}
          className={classes.button}
        >
          <ThumbsUpDown />
        </IconButton>
      </Tooltip>
    </>
  );
};

ReviewTooltip.propTypes = {
  open: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  host: PropTypes.shape({
    first_name: PropTypes.string,
  }).isRequired,
  initialValues: PropTypes.shape({
    recommended: PropTypes.bool,
    details: PropTypes.shape({
      fast: PropTypes.bool,
      friendly: PropTypes.bool,
      slow: PropTypes.bool,
      calendar_outdated: PropTypes.bool,
      price_outdated: PropTypes.bool,
    }),
    extra: PropTypes.string,
  }),
};

ReviewTooltip.defaultValues = {
  open: true,
  initialValues: {
    recommended: null,
    details: {},
    extra: '',
  },
};

export default ReviewTooltip;
